import React, { useState } from "react";
import { AppShell } from "../components/Nav";
import SectionTitle from "../components/SectionTitle";
import { prefsCriteria } from "../data/mockData";

export default function Preferences() {
  const [crit, setCrit] = useState(prefsCriteria);

  const update = (id, patch) =>
    setCrit((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14 max-w-5xl">
        <SectionTitle
          eyebrow="Preferences · the elimination logic"
          title="Tune how your journey narrows."
          subtitle="Weights control how each round scores a prospect. Must-haves are honored absolutely."
        />

        <div className="mt-10 glass rounded-3xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37]">
                Criteria weights
              </div>
              <div className="font-display text-2xl mt-1">The compass</div>
            </div>
            <button
              data-testid="reset-criteria"
              className="text-xs text-white/55 hover:text-white rounded-full border border-white/10 px-3 py-1.5"
            >
              Reset
            </button>
          </div>

          <div className="space-y-5">
            {crit.map((c) => (
              <div
                key={c.id}
                className="grid md:grid-cols-[1fr_auto_140px] items-center gap-4 md:gap-6"
              >
                <div>
                  <div className="font-display text-lg">{c.label}</div>
                  <div className="text-[11px] text-white/45 tracking-wider uppercase mt-0.5">
                    {c.must ? "Must have" : "Nice to have"}
                  </div>
                </div>
                <div>
                  <button
                    data-testid={`toggle-must-${c.id}`}
                    onClick={() => update(c.id, { must: !c.must })}
                    className={`rounded-full text-[11px] px-3 py-1.5 border transition-all ${
                      c.must
                        ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.08)] text-[#F2DE94]"
                        : "border-white/10 text-white/55 hover:text-white"
                    }`}
                  >
                    {c.must ? "Must-have" : "Nice-to-have"}
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={c.weight}
                    onChange={(e) => update(c.id, { weight: +e.target.value })}
                    data-testid={`weight-${c.id}`}
                    className="flex-1 accent-[#D4AF37]"
                  />
                  <span className="font-mono text-sm text-white/70 w-9 text-right">
                    {c.weight}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <div className="glass rounded-3xl p-7">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
              Hard filters
            </div>
            <div className="font-display text-2xl mb-5">Quiet thresholds</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                "Age 26–34",
                "Within 40 mi",
                "Long-term minded",
                "Wants children: open",
                "Non-smoker",
                "Actively dating",
              ].map((f) => (
                <label
                  key={f}
                  className="flex items-center gap-3 rounded-xl border border-white/10 px-3.5 py-2.5 hover:border-white/25 cursor-pointer"
                >
                  <input type="checkbox" defaultChecked className="accent-[#D4AF37]" />
                  <span className="text-sm text-white/80">{f}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="glass rounded-3xl p-7">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
              Round rules
            </div>
            <div className="font-display text-2xl mb-5">How rounds behave</div>
            <div className="space-y-3">
              {[
                ["Allow one revisit per month", true],
                ["Reveal finalist before confirmation", true],
                ["Blind chemistry round", false],
                ["AI finalist recommendation", true],
              ].map(([t, v]) => (
                <label key={t} className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
                  <span className="text-sm text-white/80">{t}</span>
                  <input type="checkbox" defaultChecked={v} className="accent-[#D4AF37]" />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 flex justify-end">
          <button
            data-testid="save-preferences"
            className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold-soft"
          >
            Save preferences
          </button>
        </div>
      </div>
    </AppShell>
  );
}
